import { ChangeDetectorRef, Component, ElementRef,
         HostListener, Inject, OnDestroy, OnInit, ViewChild
       } from '@angular/core';

import { GalleryService } from 'app/gallery/gallery.service';
import { ProductConfig } from 'app/product-designer-config';
import { Progress, ProgressService } from 'app/progress/progress.service';
import { ToolboxService } from 'app/toolboxes/toolbox.service';
import { AdditionalInfoItemConfig, AdditionalInfoItemConfigPosition,
         StepConfig,
       } from 'collage-designer/collage-designer-config';
import { PlaceholderType
       } from 'collage-designer/stage/placeholder/placeholder';
import { PlaceholderService
       } from 'collage-designer/stage/placeholder/placeholder.service';
import { TextPlaceholder
       } from 'collage-designer/stage/placeholder/text-placeholder';
import { StageComponent } from 'collage-designer/stage/stage.component';
import { StageService } from 'collage-designer/stage/stage.service';
import { GlobalConstants } from 'core/constants';
import { Globals } from 'core/globals';
import { PriceCalculationService } from 'core/price/price-calculation.service';
import { PrnglService } from 'core/prngl/prngl.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { Utils } from 'core/utils';
import { AsyncEventEmitterSubscriber } from 'core/utils/async-event-emitter';
import { of, Subscription, timer } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { DesignStepService } from './design-step.service';

export enum PreviewKind {
  webgl = 'webgl',
  prngl = 'prngl',
}

@Component({
    selector: 'design-step',
    templateUrl: './design-step.component.html',
    styleUrls: ['./design-step.component.scss'],
    standalone: false
})
export class DesignStepComponent implements OnInit, OnDestroy
{
  public readonly PreviewKind: typeof PreviewKind = PreviewKind;
  private static readonly PREVIEW_RETURN_DELAY: number = 150; /* ms */

  public designStepToolbox: string|undefined;
  public placeholderToolbox: string|undefined;
  public showImagePromoter: boolean = true;
  public previewKind: PreviewKind|undefined;
  public templateTitle: string|undefined;
  public boxPromoterTitleKey: string|undefined;
  public boxPromoterContentKey: string|undefined;
  public hasFocusedPlaceholder: boolean = false;
  public isLargeDevice: () => boolean = Utils.isLargeDevice;
  public isToolboxTransitionSuppressed: boolean = false;

  public availableToolboxes: Array<string> = [];

  @ViewChild('imageSelect') public imageSelect: ElementRef;
  @ViewChild('imagePromoter') public imagePromoter: ElementRef;

  private failedPreviewKinds: Set<PreviewKind> = new Set();
  private lastAppliedDesignStepId: number|undefined = undefined;
  private stageConfigAppliedSubscription:
      AsyncEventEmitterSubscriber<boolean>;
  private subscriptions: Subscription = new Subscription();

  public constructor(
      private cdr: ChangeDetectorRef,
      @Inject(DesignStepService) private designStepService: DesignStepService,
      @Inject(GalleryService) private galleryService: GalleryService,
      @Inject(ToolboxService) private toolboxService: ToolboxService,
      @Inject(PlaceholderService) private placeholderService:
          PlaceholderService,
      @Inject(ProductService) private productService: ProductService,
      @Inject(StageService) private stageService: StageService,
      @Inject(EventTrackingService) private eventTrackingService:
          EventTrackingService,
      @Inject(PrnglService) private prnglService: PrnglService,
  )
  {
    this.showImagePromoter = this.shouldShowImagePromoter();

    this.subscriptions.add(
      this.galleryService.imageStockChanged.subscribe(() => {
          this.showImagePromoter = this.shouldShowImagePromoter();
          this.repositionImagePromoter();
      }));

    this.subscriptions.add(
        this.toolboxService.targetChanged.subscribe(() => {
          this.designStepToolbox = this.toolboxService.getActiveToolboxName(
              'design-step');
          this.placeholderToolbox = this.toolboxService.getActiveToolboxName(
              'placeholder');
          this.showImagePromoter = this.shouldShowImagePromoter();
          this.cdr.detectChanges();
          this.repositionImagePromoter();
        }));

    this.subscriptions.add(
        this.placeholderService.focusedPlaceholderChanged.pipe(
          map(placeholder => placeholder !== undefined),
          switchMap(hasFocus => hasFocus
              ? of(true)
              : timer(DesignStepComponent.PREVIEW_RETURN_DELAY).pipe(
                  map(() => false))),
          distinctUntilChanged(),
        ).subscribe(hasFocus => {
          this.hasFocusedPlaceholder = hasFocus;
          this.cdr.detectChanges();
        }));

    this.stageConfigAppliedSubscription =
        this.stageService.stageConfigApplied.subscribe(
          async () => {
            await this.updateAvailableToolboxes();
            this.applyInitialToolbox();
          });
  }

  public get isPreviewActive(): boolean
  {
    return !!this.previewKind && !this.hasFocusedPlaceholder;
  }

  public respondToPreviewFailure(kind: PreviewKind): void
  {
    this.failedPreviewKinds.add(kind);
    this.updatePreviewAvailability();
  }

  public async ngOnInit(): Promise<void>
  {
    /**
     * Bei Klick auf die Seitenzahlen entfernt Angular entsprechende Elemente
     * aus dem DOM. Damit trotzdem geprüft werden kann, ob innerhalb einer
     * statischen Toolbox geklickt wurde, muss bereits auf ein mousedown-Event
     * gelauscht werden.
     */
    (<any>document).addEventListener(
        'touchstart', this.closeStaticToolboxes, { passive: true });
    (<any>document).addEventListener(
        'mousedown', this.closeStaticToolboxes, { passive: true });
    await this.updateAvailableToolboxes();
  }

  private async updateAvailableToolboxes(): Promise<void>
  {
    this.availableToolboxes = await this.getToolboxesNames();
    this.templateTitle = (await this.designStepService.getTemplate())?.title;

    this.updateBoxPromoter();
    this.updatePreviewAvailability();
    this.repositionImagePromoter();
  }

  private updateBoxPromoter(): void
  {
    const type = this.productService.getCurrentProduct().type;
    this.boxPromoterTitleKey = `i18n.boxPromoter.title.${
        ['puzzle', 'memo'].includes(type) ? type : 'default'}`;

    const canChooseDesign = this.isStaticToolboxAvailable('LayoutToolbox');
    const canAddText = this.isStaticToolboxAvailable('TextEditToolbox');

    if (!this.designStepService.isBoxStep()) {
      this.boxPromoterContentKey = undefined;
    }
    else if (canChooseDesign && canAddText) {
      this.boxPromoterContentKey = 'i18n.boxPromoter.content.designsAndText';
    }
    else if (canChooseDesign) {
      this.boxPromoterContentKey = 'i18n.boxPromoter.content.designs';
    }
    else if (canAddText) {
      this.boxPromoterContentKey = 'i18n.boxPromoter.content.text';
    }
    else {
      this.boxPromoterContentKey = undefined;
    }
  }

  private applyInitialToolbox(): void
  {
    const designStepId = this.designStepService.getDesignStepId();
    if (designStepId === this.lastAppliedDesignStepId) {
      return;
    }
    this.lastAppliedDesignStepId = designStepId;

    if (this.designStepService.isBoxStep() && Utils.isLargeDevice() &&
        this.previewKind && this.isStaticToolboxAvailable('LayoutToolbox')) {
      this.isToolboxTransitionSuppressed = true;
      this.toolboxService.open('LayoutToolbox');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this.isToolboxTransitionSuppressed = false;
      }));
    }
  }

  private updatePreviewAvailability(): void
  {
    this.previewKind = this.determinePreviewKind();
    this.cdr.detectChanges();
  }

  private determinePreviewKind(): PreviewKind|undefined
  {
    const isPreviewWanted =
        !!this.designStepService.getPart().previewType &&
        this.placeholderService.getImagePlaceholders().length === 0;

    if (!isPreviewWanted) {
      return undefined;
    }

    const isAvailable: Record<PreviewKind, () => boolean> = {
      [PreviewKind.webgl]: () => Utils.isWebGLAvailable(),
      [PreviewKind.prngl]: () => this.prnglService.isAvailable,
    };

    return [PreviewKind.webgl, PreviewKind.prngl].find(
        kind => !this.failedPreviewKinds.has(kind) && isAvailable[kind]());
  }

  private async getToolboxesNames(): Promise<Array<string>>
  {
    const toolboxes: Array<string> = [];
    const isGodMode = Globals.mode === GlobalConstants.MODE_GOD;

    if ((await this.designStepService.getTemplateGroups()).length > 1 ||
        Object.keys(await this.designStepService.getTemplates()).length > 1) {
      toolboxes.push('LayoutToolbox');
    }

    if (isGodMode ||
        this.placeholderService.getImagePlaceholders().length > 0) {
      toolboxes.push('GalleryToolbox');
    }

    if (isGodMode ||
        this.designStepService.getDesignStepConfig()
          .textPlaceholdersSettings.showCreateNewButton ||
        this.placeholderService.getTextPlaceholders().length > 0) {
      toolboxes.push('TextEditToolbox');
    }

    return toolboxes;
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
    this.stageService.stageConfigApplied.unsubscribe(
        this.stageConfigAppliedSubscription);

    document.removeEventListener('touchstart', this.closeStaticToolboxes);
    document.removeEventListener('mousedown', this.closeStaticToolboxes);
  }

  private closeStaticToolboxes(event): void
  {
    event.stopImmediatePropagation();

    const toolboxElements = Array.from(
        document.querySelectorAll('.toolbox.static > div'));
    const clickedAtStaticToolbox = toolboxElements
      .some(element => element.contains(event.target));

    if (!clickedAtStaticToolbox) {
      toolboxElements.map((e) => {
        let element = <HTMLElement>e;
        if (element.classList.contains('icon')
            && element.classList.contains('hide')) {
          element.classList.remove('hide');
        }
        if (element.classList.contains('content')) {
          element.classList.add('hide');
        }
      });
    }
  }

  public photosButtonClicked(): void
  {
    const shouldUploadPhotoImmediately =
      (this.galleryService.getImages().length === 0 &&
       !this.galleryService.getUploadInProgress());

    if (shouldUploadPhotoImmediately) {
      this.eventTrackingService.track({
        category: this.productService.getCurrentProduct().id,
        action: 'init_photo_upload',
        label: 'photos_button',
      });

      this.imageSelect.nativeElement.click();
    }
    this.toolboxToggle('GalleryToolbox');
  }

  public toolboxToggle(toolboxName: string): void
  {
    const focusedPlaceholder = this.placeholderService.getFocusedPlaceholder();
    if (focusedPlaceholder instanceof TextPlaceholder) {
      this.placeholderService.setFocusedPlaceholder(undefined);
    }
    this.toolboxService.toggle(toolboxName);
  }

  public get customTextPlaceholder(): TextPlaceholder|undefined
  {
    const placeholder = this.placeholderService.getSoleTextPlaceholder();

    return placeholder && !placeholder.isUnchanged() ? placeholder : undefined;
  }

  public isStaticToolboxAvailable(toolboxName: string): boolean
  {
    return this.availableToolboxes.includes(toolboxName);
  }

  // To ensure that window.innerWidth is correctly updated for device checks,
  // especially in Chrome because it fires resize events before final rendering,
  // we call requestAnimationFrame twice and listen to both events
  @HostListener('window:resize')
  @HostListener('window:orientationchange')
  public onResize(): void
  {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.repositionImagePromoter();
      });
    });
  }

  private repositionImagePromoter(): void
  {
    if (!this.showImagePromoter || !this.imagePromoter) {
      return;
    }

    if (!Utils.isLargeDevice()) {
      let translateX = 0;
      const minSpacing = 24;
      const rect = this.imagePromoter.nativeElement.getBoundingClientRect();
      if (rect.left - minSpacing < 0) {
        translateX = Math.abs(rect.left - minSpacing);
      }
      else if (rect.right + minSpacing > window.innerWidth) {
        translateX = window.innerWidth - rect.right - minSpacing;
      }

      const previousTransformX = this.imagePromoter.nativeElement.style
        .getPropertyValue('--transformx-correction');
      if (previousTransformX) {
        translateX += parseInt(previousTransformX, 10);
      }

      this.imagePromoter.nativeElement.style.setProperty(
          '--transformx-correction', translateX + 'px');
    }
  }

  private shouldShowImagePromoter(): boolean
  {
    return this.galleryService.getImages().length === 0 &&
        this.designStepToolbox !== 'GalleryToolbox';
  }
}
